import type { UserData } from "@service/AuthenticationService.type";
import { logError } from "@util/sentry-error";
import { Measurement } from "./Measurement";
import { sha256Hex } from "./sha256";

// Hashing may fail; page_init still has to go out, just without user_id.
// Only the error object may reach Sentry — never a user field.
async function hashEmail(email: string): Promise<string | undefined> {
	try {
		return await sha256Hex(email);
	} catch (error) {
		logError(error as Error);

		return undefined;
	}
}

// The user fields go into the dataLayer raw on purpose; keeping them out of GA4 is GTM's responsibility, not this code's.
async function getUserParameters(user?: UserData): Promise<Record<string, unknown>> {
	if (!user?.email) {
		return {};
	}

	// phone_number is deliberately absent: /customer returns the phone masked for the MFA screen
	// ("+36 30*****316"), so it could never match anything downstream. Sending an unmatchable
	// phone number would be personal data at no benefit. It can only come back with an unmasked
	// value from the backend.
	return {
		// biome-ignore lint/style/useNamingConvention: GA4 dataLayer parameter names
		user_id: await hashEmail(user.email),
		email: user.email,
		first_name: user.firstName,
		last_name: user.lastName,
	};
}

// The consultantId only ever exists as a URL parameter, so it is sent only when present.
function getConsultantId(): string | undefined {
	return new URLSearchParams(window.location.search).get("consultantId") || undefined;
}

/**
 * GA4 has to learn the user_id at the tag/config level, not just inside a dataLayer event object — a value that
 * only ever rides along with the specific push it happens to sit in never reaches the hits GTM fires afterwards,
 * which is exactly the coverage bug IT-14910 reports (BM_lead never carrying one). gtag("set", ...) is Google's
 * documented way to make it stick for the rest of the session, so this must run before the first event goes out
 * below, not after.
 */
function setGa4UserId(userId: unknown): void {
	if (typeof userId === "string" && userId !== "") {
		window.gtag?.("set", "user_id", userId);
	}
}

async function pushPageInit(user?: UserData): Promise<void> {
	const signedIn = !!user?.email;

	Measurement.setSignedIn(signedIn);

	const consultantId = getConsultantId();
	const userParameters = await getUserParameters(user);

	setGa4UserId(userParameters.user_id);

	Measurement.sendEvent("page_init", {
		...userParameters,
		// biome-ignore lint/style/useNamingConvention: GA4 dataLayer parameter name
		...(consultantId && { consultant_id: consultantId }),
	});
}

// Callers deliberately do not await, so the rejection handler lives here where no caller can forget it.
// Only the error object may reach Sentry — never a user field.
async function pushPageInitSafely(user?: UserData): Promise<void> {
	try {
		await pushPageInit(user);
	} catch (error) {
		logError(error as Error);
	}
}

let authStateKnown = false;
let signedIn = false;

// `authenticated` is authoritative: while it is false no user field may go out whatever `user` holds — the fields are
// raw PII and the user is not signed in until MFA succeeds. Enforced here so a caller cannot drop it.
export async function reportAuthState(user: UserData | undefined, authenticated: boolean): Promise<void> {
	const effectiveUser = authenticated ? user : undefined;
	const nowSignedIn = !!effectiveUser?.email;
	const firstReport = !authStateKnown;

	if (!firstReport && nowSignedIn === signedIn) {
		return;
	}

	authStateKnown = true;
	signedIn = nowSignedIn;

	if (firstReport || nowSignedIn) {
		await pushPageInitSafely(effectiveUser);

		// Only a sign-in that happens while the page is open is a login; arriving already signed in is a restored session.
		// This is also the first moment the user counts as signed in, because the app requires MFA before it says so.
		if (!firstReport) {
			Measurement.sendEvent("BM_login", { action: "password" });
		}

		return;
	}

	Measurement.setSignedIn(false);
	Measurement.sendEvent("BM_logout");
}
